/* eslint-disable react/no-array-index-key */
import React, { ReactElement } from 'react'
import { useSelector } from 'react-redux'
import { BiMap, BiSolidTime, BiGlobe, BiMessageRounded, BiPhone, BiNavigation } from 'react-icons/bi'
import { IconType } from 'react-icons'
import styled from '../../utils/styled'
import { Typography } from '../layout/Typography'
import { SalonAddress, SalonOpeningHours } from '../../store/salon'
import { Box } from '../layout/Box'
import { ApplicationState } from '../../store'

const formatAddress = (address?: SalonAddress) => {
  if (!address) {
    return ''
  }
  return `${address.strasse}, ${address.plz} ${address.stadt}`
}

const formatOpeningTime = (openingTime: string) => {
  return openingTime
    .split(':')
    .slice(0, 2)
    .join(':')
}

const formatDayName = (dayId: number) => {
  switch (dayId) {
    case 1:
      return 'Montag'
    case 2:
      return 'Dienstag'
    case 3:
      return 'Mittwoch'
    case 4:
      return 'Donnerstag'
    case 5:
      return 'Freitag'
    case 6:
      return 'Samstag'
    case 7:
      return 'Sonntag'
    default:
      return ''
  }
}

function createGoogleMapLink(street: string, city: string, postalCode: string) {
  // Constructing the query string
  const query = `${street}, ${city}, ${postalCode}`

  // Encoding the query string for use in URL
  const encodedQuery = encodeURIComponent(query)

  // Constructing the Google Maps link
  const mapLink = `https://www.google.com/maps/search/?api=1&query=${encodedQuery}`

  return mapLink
}

const HeaderOpeningHours = ({ openingHours }: { openingHours: SalonOpeningHours[] }): ReactElement => {
  // Separate weekdays into groups with consistent opening and closing times
  const weekdayGroups: SalonOpeningHours[][] = []
  let currentGroup: SalonOpeningHours[] = []
  openingHours.forEach(day => {
    if (
      currentGroup.length === 0 ||
      currentGroup[currentGroup.length - 1].schließzeit !== day.schließzeit ||
      currentGroup[currentGroup.length - 1].öffnungszeit !== day.öffnungszeit
    ) {
      currentGroup = [day]
      weekdayGroups.push(currentGroup)
    } else {
      currentGroup.push(day)
    }
  })

  return (
    <Box flex flexDirection="row">
      <Box flex flexDirection="column">
        {weekdayGroups.map((openingHour, index) => (
          <Typography key={`weekdayName-${index}`} variant="body1" margin="0">
            {openingHour.length > 1
              ? `${formatDayName(openingHour[0].refTageId)} - ${formatDayName(openingHour[openingHour.length - 1].refTageId)}`
              : formatDayName(openingHour[0].refTageId)}
          </Typography>
        ))}
      </Box>
      <Box flexDirection="column" margin="0 0 0 12px">
        {weekdayGroups.map((openingHour, index) => (
          <Typography key={`weekdayOpeningHours-${index}`} variant="body1" margin="0">
            | {formatOpeningTime(openingHour[0].öffnungszeit)} - {formatOpeningTime(openingHour[0].schließzeit)}
          </Typography>
        ))}
      </Box>
    </Box>
  )
}

const HeaderInfoElement = ({ Icon, label }: { Icon: IconType; label: string | ReactElement }) => {
  return (
    <Box flex flexDirection="row" alignItems="center" margin="0 0 18px">
      <Box margin="3px 10px 0 0">
        <StyledIcon Icon={Icon} size={16} />
      </Box>
      {typeof label === 'string' ? (
        <Typography variant="body1" margin="0">
          {label}
        </Typography>
      ) : (
        label
      )}
    </Box>
  )
}

const HeaderInfoButton = ({ Icon, label, size, href }: { Icon: IconType; label: string; size: number; href: string }) => {
  return (
    <HeaderButtonContainer>
      <HeaderButton target="_blank" href={href}>
        <StyledIcon Icon={Icon} size={size} />
      </HeaderButton>
      <Typography variant="body2" margin="0">
        {label}
      </Typography>
    </HeaderButtonContainer>
  )
}

const BANNER_IMAGE_HEIGHT = 200

export const SalonDetails = () => {
  const { data: salonData } = useSelector((state: ApplicationState) => state.salon)

  if (!salonData) {
    return null
  }

  return (
    <SalonDetailsContainer>
      {salonData.headImgPath && salonData.headImgPath && (
        <BannerImageContainer>
          <BannerImage src={salonData.headImgPath} />
        </BannerImageContainer>
      )}
      <InfoContainerWrapper>
        <InfoContainer>
          <Box>
            <SalonLogo src={salonData.logoPath} />
            <HeaderInfoElement Icon={BiMap} label={formatAddress(salonData.address)} />
            <HeaderInfoElement Icon={BiSolidTime} label={<HeaderOpeningHours openingHours={salonData.öffnungszeiten} />} />
          </Box>
          <HeaderButtonsRow>
            <HeaderInfoButton Icon={BiGlobe} label="Website" size={24} href={salonData.domain} />
            <HeaderInfoButton Icon={BiMessageRounded} label="Nachricht" size={24} href={salonData.kontakt.whatsApp} />
            <HeaderInfoButton Icon={BiPhone} label="Aufruf" size={24} href={`tel:${salonData.kontakt.tel}`} />
            <HeaderInfoButton
              Icon={BiNavigation}
              label="Navigation"
              size={24}
              href={createGoogleMapLink(salonData.address.strasse, salonData.address.stadt, salonData.address.plz)}
            />
          </HeaderButtonsRow>
        </InfoContainer>
      </InfoContainerWrapper>
    </SalonDetailsContainer>
  )
}

const SalonDetailsContainer = styled('div')`
  padding-top: ${props => props.theme.containerPadding};
`

const BannerImageContainer = styled('div')`
  margin-top: calc(-${props => props.theme.containerPadding} - ${props => props.theme.containerPadding});
  margin-left: -${props => props.theme.containerPadding};
  margin-right: -${props => props.theme.containerPadding};
`

const BannerImage = styled('img')`
  width: 100%;
  max-height: ${BANNER_IMAGE_HEIGHT}px;
  object-fit: cover;
`

const InfoContainerWrapper = styled('div')`
  background-color: ${props => props.theme.colors.background};
  position: relative;
  z-index: 1;
  border-top-left-radius: 30px;
  border-top-right-radius: 30px;
  margin-top: -30px;
  margin-left: -${props => props.theme.containerPadding};
  margin-right: -${props => props.theme.containerPadding};
`

const InfoContainer = styled('div')`
  margin-left: ${props => props.theme.containerPadding};
  margin-right: ${props => props.theme.containerPadding};
`

const HeaderButtonsRow = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0 20px;
  width: 100%;
`

const HeaderButtonContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 10px;
`

const HeaderButton = styled('a')`
  display: flex;
  width: 42px;
  height: 42px;
  border: 1px solid ${props => props.theme.colors.black};
  border-radius: 42px;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;
`

const StyledIcon = ({ Icon, size }: { Icon: IconType; size: number }) => {
  const NamedStyledIcon = styled(Icon)<{ size: number }>`
    color: ${props => props.theme.colors.black};
    line-height: ${props => `${props.size}px`};
  `
  return <NamedStyledIcon size={size} />
}

const SalonLogo = styled('img')`
  margin: 28px 12.5% 32px;
  width: 75%;
  height: 80px;
  object-fit: contain;
`
